import { useState } from 'react';
import { IconChevronDown, IconDeviceMobileShare, IconMail, IconPrinter } from '@tabler/icons-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import Hint from '../../../../../components/common/Hint';
import { BRAND_MARKS } from './qrBrandMarks';

/** Brand mark in its own colour; the same paths the type picker uses. */
function BrandMark({ mark }) {
    return (
        <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true">
            <path d={mark.path} fill={mark.hex} />
        </svg>
    );
}

/** Where a finished code can be sent. */
const SHARE_TARGETS = [
    {
        id: 'whatsapp',
        tint: 'bg-[#E3F6EC]',
        render: () => <BrandMark mark={BRAND_MARKS.whatsapp} />,
    },
    {
        id: 'facebook',
        tint: 'bg-[#EAF1FE]',
        render: () => <BrandMark mark={BRAND_MARKS.facebook} />,
    },
    {
        // No brand mark for "any mail app", so these two keep a line icon.
        id: 'email',
        tint: 'bg-[#FFF1D9] text-[#C98A11]',
        render: () => <IconMail size={17} stroke={1.9} />,
    },
    {
        id: 'device',
        tint: 'bg-[#F0ECFF] text-[#6D5BD0]',
        render: () => <IconDeviceMobileShare size={17} stroke={1.9} />,
    },
];

/** Beyond this each copy is smaller than a postage stamp on A4. */
const MAX_PER_PAGE = 64;
/** Counts offered as buttons; anything else is typed into the box beside them. */
const PER_PAGE_PRESETS = [1, 2, 4, 9];

/**
 * The print sheet and the share targets, as one button in the sticky action bar.
 *
 * Both used to sit in the preview card, below the artwork and off the bottom of
 * a tall page. They are things you do with a finished code, so they belong with
 * the other actions — but a per-page selector and four share tiles cannot sit on
 * one bar row, so the button opens them in a popover instead.
 *
 * `perPage` lives here because nothing else needs it: it is only ever read on
 * the way into the print sheet.
 */
export default function QrPrintShare({ ready, onPrint, onShare }) {
    const { t } = useTranslation();
    const [perPage, setPerPage] = useState(1);
    const [open, setOpen] = useState(false);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    disabled={!ready}
                    className="inline-flex h-[46px] items-center gap-2 rounded-xl border border-[#ECE8E4] bg-white px-[18px] text-[14.5px] font-semibold text-[#3D3833] transition-colors hover:border-primary/40 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                >
                    <IconPrinter size={18} stroke={1.8} />
                    {t('qrCode.outputTitle')}
                    <IconChevronDown
                        size={15}
                        className={`flex-none text-[#A8A099] transition-transform ${open ? 'rotate-180' : ''}`}
                    />
                </button>
            </PopoverTrigger>

            {/* Opens upward: the bar it hangs off is pinned to the bottom. */}
            <PopoverContent side="top" align="end" className="w-[320px] p-4">
                <div className="space-y-4">
                    <div>
                        <div className="mb-2 flex items-baseline justify-between gap-2 text-[12.5px] font-semibold text-[#6B635C] dark:text-zinc-400">
                            <span>{t('qrCode.printSheet')}</span>
                            <span className="text-[11px] font-medium text-[#6B635B]">
                                {t('qrCode.printSheetRange', { max: toBnDigits(MAX_PER_PAGE) })}
                            </span>
                        </div>
                        {/* Common counts as one-tap presets, with the last slot
                            free for any other number. */}
                        <div className="flex gap-1 rounded-xl bg-[#F7F5F3] p-1 dark:bg-zinc-800">
                            {PER_PAGE_PRESETS.map((count) => (
                                <button
                                    key={count}
                                    type="button"
                                    onClick={() => setPerPage(count)}
                                    aria-pressed={perPage === count}
                                    className={`flex-1 rounded-[9px] py-2 text-[12.5px] font-semibold transition-colors ${
                                        perPage === count
                                            ? 'bg-white text-[#1C1815] shadow-[0_1px_3px_rgba(28,24,21,0.10)] dark:bg-zinc-900 dark:text-zinc-100'
                                            : 'text-[#6B635C] hover:text-[#3D3833] dark:text-zinc-400'
                                    }`}
                                >
                                    {toBnDigits(count)}
                                </button>
                            ))}
                            <input
                                type="number"
                                min={1}
                                max={MAX_PER_PAGE}
                                step={1}
                                // Empty while a preset is active, so the box reads
                                // as "or type your own".
                                value={PER_PAGE_PRESETS.includes(perPage) ? '' : perPage}
                                // Clamped on the way out, so a typed 999 cannot
                                // reach the sheet builder.
                                onChange={(e) => {
                                    const next = Number(e.target.value);
                                    if (!e.target.value) return;
                                    setPerPage(Number.isFinite(next) ? Math.min(MAX_PER_PAGE, Math.max(1, Math.round(next))) : 1);
                                }}
                                placeholder={t('qrCode.printCustom')}
                                aria-label={t('qrCode.printCustom')}
                                // Dashed at rest so it reads as typeable next to the
                                // buttons; on focus it becomes the same white pill as
                                // a picked preset, plus a ring.
                                className={`w-16 flex-none rounded-[9px] border border-dashed px-2 text-center text-[12.5px] font-semibold outline-none transition-all placeholder:font-medium placeholder:text-[#A8A099] focus:border-solid focus:border-primary focus:bg-white focus:text-[#1C1815] focus:shadow-[0_0_0_3px_rgba(230,138,0,0.18)] dark:focus:bg-zinc-900 dark:focus:text-zinc-100 ${
                                    PER_PAGE_PRESETS.includes(perPage)
                                        ? 'border-[#DCD6D0] bg-transparent text-[#6B635C] hover:bg-white/60 dark:border-zinc-600 dark:text-zinc-400'
                                        : 'border-solid border-primary bg-white text-[#1C1815] shadow-[0_1px_3px_rgba(28,24,21,0.10)] dark:bg-zinc-900 dark:text-zinc-100'
                                }`}
                            />
                        </div>
                        <button
                            type="button"
                            onClick={() => { setOpen(false); onPrint(perPage); }}
                            disabled={!ready}
                            className="mt-2.5 flex h-11 w-full items-center justify-center gap-2 rounded-[13px] border border-[#ECE8E4] bg-white text-[13.5px] font-semibold text-[#3D3833] transition-colors hover:border-primary/40 hover:text-primary disabled:cursor-not-allowed disabled:text-[#A8A099] dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300"
                        >
                            <IconPrinter size={16} stroke={1.8} />
                            {t('qrCode.printOpen')}
                        </button>
                    </div>

                    <div>
                        <div className="mb-2 text-[12.5px] font-semibold text-[#6B635C] dark:text-zinc-400">{t('qrCode.shareTitle')}</div>
                        <div className="grid grid-cols-4 gap-2">
                            {SHARE_TARGETS.map(({ id, render, tint }) => (
                                <Hint label={t(`qrCode.share.${id}`)} key={id}>
                                <button
                                    type="button"
                                    onClick={() => { setOpen(false); onShare(id); }}
                                    disabled={!ready}
                                    aria-label={t(`qrCode.share.${id}`)}
                                    className="flex flex-col items-center gap-1.5 rounded-[13px] border border-[#ECE8E4] bg-white px-1 py-2.5 transition-colors hover:border-primary/40 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900"
                                >
                                    <span className={`flex h-[30px] w-[30px] items-center justify-center rounded-[10px] ${tint}`}>
                                        {render()}
                                    </span>
                                    <span className="text-[10.5px] font-semibold text-[#6B635C] dark:text-zinc-400">
                                        {t(`qrCode.share.${id}`)}
                                    </span>
                                </button>
                                </Hint>
                            ))}
                        </div>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
}
